import { createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { RemoteIOModels } from '@models/remote-io.models';
import { RemoteIOActions } from '@states/remote-io/remote-io.action-types';

export interface RemoteIOState extends EntityState<RemoteIOModels.RemoteIODocument> {
  isInitialLoaded: boolean;
  isLoading: boolean;
  filters: {
    query: string
  };
  isSaving: boolean;
  selectedRemoteIODocument: RemoteIOModels.RemoteIODocument;
  isFormValid: boolean;
  remoteIOConnected: boolean;
  testPassed: boolean;
  testFinished: boolean;
  testLoading: boolean;
  connectionFieldsChanged: boolean;
  isDeleting: boolean;
}

export const adapter: EntityAdapter<RemoteIOModels.RemoteIODocument> = createEntityAdapter<RemoteIOModels.RemoteIODocument>({
  selectId: (document: RemoteIOModels.RemoteIODocument) => document._id,
});

export const initialState: RemoteIOState = adapter.getInitialState({
  isInitialLoaded: false,
  isLoading: false,
  filters: {
    query: null,
  },
  isSaving: false,
  selectedRemoteIODocument: null,
  isFormValid: false,
  remoteIOConnected: false,
  testPassed: false,
  testFinished: false,
  testLoading: false,
  connectionFieldsChanged: false,
  isDeleting: false,
});

export const remoteIOStateReducer = createReducer(
  initialState,
  on(RemoteIOActions.resetToInitialState, state => {
    return {
      ...initialState,
    };
  }),
  on(RemoteIOActions.getRemoteIOs, state => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(RemoteIOActions.getRemoteIOsFail, state => {
    return {
      ...state,
      isLoading: false,
      isInitialLoaded: true
    };
  }),
  on(RemoteIOActions.getRemoteIOsSuccess, (state, { documents }) => {
    return adapter.setMany(documents, {
      ...state,
      isLoading: false,
      isInitialLoaded: true,
    });
  }),
  on(RemoteIOActions.setSelectedRemoteIO, (state, { document }) => {
    return {
      ...state,
      selectedRemoteIODocument: document,
    };
  }),
  on(RemoteIOActions.setQueryStringFilter, (state, { prop, value }) => {
    return {
      ...state,
      isLoading: true,
      filters: {
        ...state.filters,
        [prop]: value,
      },
    };
  }),
  on(RemoteIOActions.resetEntities, (state) => {
    return {
      ...state,
      entities: initialState.entities,
    };
  }),
  on(RemoteIOActions.testRemoteIOSuccess, (state) => {
    return {
      ...state,
      remoteIOConnected: true,
    };
  }),
  on(RemoteIOActions.setIsSaving, (state, { isSaving }) => {
    return {
      ...state,
      isSaving: isSaving,
    };
  }),
  on(RemoteIOActions.startDeleteRemoteIO, (state) => {
    return {
      ...state,
      isDeleting: true,
    };
  }),
  on(RemoteIOActions.deleteRemoteIOSuccess, (state, { remoteIOId }) => {
    return adapter.removeOne(remoteIOId, {
      ...state,
      isDeleting: false,
    });
  }),
  on(RemoteIOActions.deleteRemoteIOFail, (state) => {
    return {
      ...state,
      isDeleting: false,
    };
  }),
  on(RemoteIOActions.getOneRemoteIOSuccess, (state, { document }) => {
    return {
      ...state,
      selectedRemoteIODocument: document,
    };
  }),
  on(RemoteIOActions.changeFormValidation, (state, { isFormValid }) => {
    return {
      ...state,
      isFormValid,
    };
  }),
  on(RemoteIOActions.resetTestState, (state) => {
    return {
      ...state,
      testPassed: false,
      testFinished: false,
      connectionFieldsChanged: false,
    };
  }),
  on(RemoteIOActions.setTestStateInEditMode, (state) => {
    return {
      ...state,
      testPassed: true,
      testFinished: true,
      connectionFieldsChanged: false,
    };
  }),
  on(RemoteIOActions.setTestStateInCreateMode, (state) => {
    return {
      ...state,
      testPassed: false,
      testFinished: false,
    };
  }),
  on(RemoteIOActions.setTestStateOnFieldsChanged, (state) => {
    return {
      ...state,
      testPassed: false,
      testFinished: false,
      connectionFieldsChanged: true,
    };
  }),
  on(RemoteIOActions.setTestStateOnSuccess, (state) => {
    return {
      ...state,
      testPassed: true,
      testLoading: false,
      testFinished: true,
      connectionFieldsChanged: false,
    };
  }),
  on(RemoteIOActions.setTestStateOnRequestStart, (state) => {
    return {
      ...state,
      testPassed: false,
      testLoading: true,
      testFinished: false,
    };
  }),
  on(RemoteIOActions.setTestStateOnRequest, (state, { isTestOk }) => {
    return {
      ...state,
      testPassed: isTestOk,
      testLoading: false,
      testFinished: true,
      connectionFieldsChanged: false,
    };
  }),
);
